import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal } from './ui/Modal';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Badge } from './ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from './ui/Table';
import ConfirmModal from './ConfirmModal';
import api from '../api/client';

const AdminPanel = ({ onClose, onImpersonate }) => {
  const [users, setUsers] = useState([]);
  const [newUsername, setNewUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [createError, setCreateError] = useState('');
  const [error, setError] = useState('');
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const copiedTimerRef = useRef(null);

  useEffect(() => () => clearTimeout(copiedTimerRef.current), []);

  const loadUsers = useCallback(async () => {
    try {
      const resp = await api.get('/admin/users');
      setUsers(resp.data);
    } catch {
      setError('Ошибка загрузки пользователей');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateError('');
    if (!newUsername || newUsername.length < 3) {
      setCreateError('Минимум 3 символа');
      return;
    }
    try {
      const resp = await api.post('/admin/users', { username: newUsername });
      setInviteLink(resp.data.invite_link);
      setCopied(false);
      setNewUsername('');
      loadUsers();
    } catch (err) {
      setCreateError(err?.response?.data?.detail || 'Ошибка создания');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/users/${deleteTarget.id}`);
      setDeleteTarget(null);
      loadUsers();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Ошибка удаления');
      setDeleteTarget(null);
    }
  };

  const handleImpersonate = async (user) => {
    try {
      const resp = await api.post(`/admin/impersonate/${user.id}`);
      onImpersonate(resp.data.access_token, user.username);
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Ошибка имперсонации');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    clearTimeout(copiedTimerRef.current);
    copiedTimerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  // Пока открыто подтверждение удаления, Escape/фон не должны закрывать панель.
  const handlePanelClose = () => {
    if (!deleteTarget) onClose();
  };

  return (
    <>
      <Modal
        open
        onClose={handlePanelClose}
        title="Управление пользователями"
        subtitle="Создание, приглашения и доступ"
        icon="bi-people"
        size="lg"
      >
        <div className="space-y-2xl">
          {/* Создание пользователя */}
          <form onSubmit={handleCreate} className="flex items-start gap-md">
            <div className="flex-1 min-w-0">
              <Input
                label="Имя пользователя"
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                error={createError}
              />
            </div>
            <Button type="submit" variant="primary" icon="bi-person-plus" className="shrink-0 mt-[20px]">
              Создать
            </Button>
          </form>

          {/* Готовая invite-ссылка */}
          {inviteLink && (
            <div className="p-lg rounded-md border border-success/30 bg-success-soft space-y-md animate-fade-in">
              <p className="text-body-xs font-medium text-success flex items-center gap-sm">
                <i className="bi bi-link-45deg" aria-hidden="true" />
                Ссылка-приглашение
              </p>
              <div className="flex items-start gap-md">
                <code className="flex-1 min-w-0 font-mono tabular text-body-xs text-text bg-surface-raised border border-border rounded-md px-md py-md break-all">
                  {inviteLink}
                </code>
                <Button
                  variant={copied ? 'ghost' : 'secondary'}
                  size="sm"
                  icon={copied ? 'bi-check-lg' : 'bi-clipboard'}
                  onClick={copyLink}
                  className="shrink-0"
                >
                  {copied ? 'Скопировано' : 'Копировать'}
                </Button>
              </div>
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="flex items-start gap-md p-lg rounded-md bg-destructive-soft border border-destructive/30 animate-fade-in"
            >
              <i className="bi bi-exclamation-triangle text-destructive" aria-hidden="true" />
              <p className="text-body-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Список пользователей */}
          {loading ? (
            <div className="space-y-md" aria-hidden="true">
              {[1, 2, 3].map((i) => (
                <div key={i} className="skeleton h-9 w-full rounded-md" />
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-3xl">
              <i className="bi bi-people text-2xl text-text-muted" aria-hidden="true" />
              <p className="text-body-sm text-text-muted mt-md">Пользователей пока нет</p>
            </div>
          ) : (
            <div className="border border-border rounded-md overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Пользователь</TableHead>
                    <TableHead>Роль</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead className="text-right">Действия</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id}>
                      <TableCell className="font-medium text-text-strong">{u.username}</TableCell>
                      <TableCell>
                        {u.role === 'admin' ? (
                          <Badge variant="primary" icon="bi-shield-lock">admin</Badge>
                        ) : (
                          <Badge variant="default" icon="bi-person">{u.role}</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {u.pending_invite ? (
                          <Badge variant="outline" icon="bi-hourglass-split">Ожидает приглашения</Badge>
                        ) : (
                          <Badge variant="success" icon="bi-check-circle">Активен</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {u.role !== 'admin' && (
                          <div className="inline-flex items-center gap-md">
                            <Button
                              variant="secondary"
                              size="sm"
                              icon="bi-eye"
                              onClick={() => handleImpersonate(u)}
                              title="Смотреть как пользователь"
                            >
                              Смотреть
                            </Button>
                            <Button
                              variant="danger"
                              size="sm"
                              icon="bi-trash"
                              onClick={() => setDeleteTarget(u)}
                              title="Удалить пользователя"
                            >
                              Удалить
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </Modal>

      {/* Подтверждение удаления */}
      {deleteTarget && (
        <ConfirmModal
          title="Удаление пользователя"
          message={`Удалить пользователя «${deleteTarget.username}» и все его данные? Это действие необратимо.`}
          confirmLabel="Удалить"
          danger
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
};

export default AdminPanel;
